import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { CheckCircle2, AlertTriangle, Star, CalendarDays, MessageSquare, Edit2, Plus } from 'lucide-react';
import { format } from 'date-fns';

export const FeedbackTab = ({ sessions, feedbackSummary, canComplete, onOpenSessionFeedback, onOpenProjectFeedback }) => (
  <div className="space-y-6">
    {canComplete && canComplete.can_complete && feedbackSummary?.has_project_feedback && (
      <Alert className="border-green-200 bg-green-50">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          All feedback submitted. This project can be marked as completed.
        </AlertDescription>
      </Alert>
    )}

    {/* Session Feedback */}
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Session Feedback</span>
          <Badge variant="outline">
            {feedbackSummary?.sessions_with_feedback || 0} / {sessions.length} completed
          </Badge>
        </CardTitle>
        <CardDescription>
          Submit feedback for each training session after delivery
        </CardDescription>
      </CardHeader>
      <CardContent>
        {sessions.length > 0 ? (
          <div className="space-y-3">
            {sessions.map((session) => {
              const hasFeedback = feedbackSummary?.session_feedback?.some(
                f => f.session_id === session.id
              );
              const sessionFb = feedbackSummary?.session_feedback?.find(
                f => f.session_id === session.id
              );

              return (
                <div
                  key={session.id}
                  className={`flex items-center justify-between p-4 border rounded-lg ${hasFeedback ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}
                >
                  <div className="flex items-center gap-3">
                    {hasFeedback ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                    )}
                    <div>
                      <p className="font-medium">{session.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {session.session_date ? format(new Date(session.session_date), 'MMM d, yyyy') : 'No date'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {hasFeedback && sessionFb && (sessionFb.mtm_trainer_rating || sessionFb.mtm_course_rating) && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-sm font-medium">
                          {sessionFb.mtm_trainer_rating && sessionFb.mtm_course_rating
                            ? ((sessionFb.mtm_trainer_rating + sessionFb.mtm_course_rating) / 2).toFixed(1)
                            : (sessionFb.mtm_trainer_rating || sessionFb.mtm_course_rating)}/5
                        </span>
                      </div>
                    )}
                    <Button
                      variant={hasFeedback ? "outline" : "default"}
                      size="sm"
                      onClick={() => onOpenSessionFeedback(session)}
                      className={hasFeedback ? "" : "bg-lime text-lime-foreground hover:bg-lime/90"}
                    >
                      {hasFeedback ? 'Edit Feedback' : 'Add Feedback'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No sessions to provide feedback for</p>
          </div>
        )}

        {/* Average Ratings */}
        {feedbackSummary?.average_ratings?.overall > 0 && (
          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium mb-4">Average MTM Ratings</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">MTM Trainer</p>
                <div className="flex items-center justify-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-lg font-bold">{feedbackSummary.average_ratings.mtm_trainer}/5</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">MTM Course</p>
                <div className="flex items-center justify-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-lg font-bold">{feedbackSummary.average_ratings.mtm_course}/5</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Overall Avg</p>
                <div className="flex items-center justify-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-lg font-bold">{feedbackSummary.average_ratings.overall}/5</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Project Feedback */}
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Overall Project Feedback</span>
          {feedbackSummary?.has_project_feedback ? (
            <Badge className="bg-green-100 text-green-800">Completed</Badge>
          ) : (
            <Badge variant="outline" className="text-muted-foreground">Optional</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Provide overall feedback for the entire training project
        </CardDescription>
      </CardHeader>
      <CardContent>
        {feedbackSummary?.project_feedback ? (
          <div className="space-y-4">
            {feedbackSummary.project_feedback.key_achievements && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Key Highlights / Achievements</p>
                <p className="mt-1 whitespace-pre-wrap">{feedbackSummary.project_feedback.key_achievements}</p>
              </div>
            )}
            {feedbackSummary.project_feedback.challenges_faced && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Challenges Faced</p>
                <p className="mt-1 whitespace-pre-wrap">{feedbackSummary.project_feedback.challenges_faced}</p>
              </div>
            )}
            {feedbackSummary.project_feedback.lessons_learned && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Lessons Learned</p>
                <p className="mt-1 whitespace-pre-wrap">{feedbackSummary.project_feedback.lessons_learned}</p>
              </div>
            )}
            {feedbackSummary.project_feedback.mtm_feedback_score != null && (
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                <span className="text-sm font-medium">MTM Score: {feedbackSummary.project_feedback.mtm_feedback_score}/5</span>
              </div>
            )}
            <div className="flex justify-end">
              <Button variant="outline" onClick={onOpenProjectFeedback}>
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Feedback
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No overall feedback submitted yet</p>
            <Button onClick={onOpenProjectFeedback} className="bg-lime text-lime-foreground hover:bg-lime/90">
              <Plus className="w-4 h-4 mr-2" />
              Add Project Feedback
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

export default FeedbackTab;
